import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { spacing } from '@/theme/tokens';

interface SectionProps {
  /** Small uppercase label above the heading. */
  readonly eyebrow?: string;
  /** Section heading. Omit for sections that continue the previous one. */
  readonly title?: string;
  /** Optional standfirst beneath the heading. */
  readonly lead?: string;
  /** Renders the section on the sunken surface, to break up a long page. */
  readonly tinted?: boolean;
  /** Heading level. Every page has exactly one `h1`; sections default to `h2`. */
  readonly component?: 'h2' | 'h3';
  readonly children?: ReactNode;
}

/**
 * A page section with consistent vertical rhythm and heading treatment.
 *
 * Pages compose these rather than setting their own padding, which is what stops the
 * spacing drifting page by page.
 */
export function Section({
  eyebrow,
  title,
  lead,
  tinted = false,
  component = 'h2',
  children,
}: SectionProps) {
  return (
    <Box
      component="section"
      sx={{
        py: spacing.sectionY,
        ...(tinted && {
          bgcolor: 'background.paper',
          borderBlock: 1,
          borderColor: 'divider',
        }),
      }}
    >
      <Container>
        {(eyebrow ?? title ?? lead) ? (
          <Stack spacing={2} sx={{ mb: { xs: 5, md: 8 }, maxWidth: 720 }}>
            {eyebrow ? (
              <Typography variant="overline" color="secondary.main">
                {eyebrow}
              </Typography>
            ) : null}
            {title ? (
              <Typography variant={component === 'h2' ? 'h2' : 'h3'} component={component}>
                {title}
              </Typography>
            ) : null}
            {lead ? (
              <Typography variant="subtitle1" color="text.secondary">
                {lead}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {children}
      </Container>
    </Box>
  );
}
